import React, { MouseEvent, useEffect, useState } from 'react';
import { createPopper, Instance } from '@popperjs/core';
import { useTranslation } from 'react-i18next';
import { CollapsibleContent } from '../../../organisms/CollapsibleOverlay/CollapsibleOverlay';
import useCollapsibleOverlay from '../../../../hooks/useCollapsibleOverlay';
import FormsRow from '../../Form/components/FormsRow/FormsRow';
import FormSubmitOrCancel from '../../Form/components/FormSubmitOrCancel/FormSubmitOrCancel';
import { TopUserResult, UserSingleSelectComboBoxForm2 } from '../../Form/components/ComboBox';
import { Namespace } from '../../../../translation/namespace';
import { FilterButton } from './FilterButton';
import { FilterContainer } from './FilterContainer';

type FilterPersonProps = {
  active: boolean;
  onSubmit: (query: Record<string, string>) => void;
  facetKey: string;
  valueKey: string;
  searchTerm: string;
  query: Record<string, string>;
  suggestUrl: string;
};

export function FilterPerson({
  active,
  onSubmit,
  facetKey,
  valueKey,
  searchTerm,
  query,
  suggestUrl,
}: FilterPersonProps) {
  const { collapsibleOpen, focusToggle, toggleCollapsible, toggleProps, collapsibleProps } = useCollapsibleOverlay();
  const { t } = useTranslation(`${Namespace.ResultListSettings}`);

  const [person, setPerson] = useState<string>('');
  const setSelectedPerson = (item: TopUserResult | undefined) => {
    if (item) {
      // @ts-expect-error TopUserResult type can't anticipate unknown string key.
      setPerson(item[valueKey]);
    }
  };

  const handleSubmit = () => {
    // creating the queryobject needed for filtering
    onSubmit({
      ...query,
      [facetKey]: person,
    });
  };

  const acceptAction = (event: MouseEvent) => {
    if (event) {
      event.preventDefault();
    }
    handleSubmit();
    toggleCollapsible();
  };

  const closeAndFocusButton = () => {
    toggleCollapsible();
    if (toggleProps.ref.current) {
      focusToggle();
    }
  };

  useEffect(() => {
    let popper: Instance;

    if (collapsibleOpen) {
      // using timeout here to tackle a race condition between popper and the Browser's popover API
      setTimeout(() => {
        const collapsible = document.getElementById(collapsibleProps.id);

        if (toggleProps.ref?.current && collapsible) {
          popper = createPopper(toggleProps.ref.current, collapsible, {
            modifiers: [
              { name: 'preventOverflow', enabled: false },
              { name: 'hide', enabled: false },
              { name: 'offset', options: { offset: [0, 1] } },
            ],
          });
        }
      }, 0);
    }

    return () => {
      if (popper) {
        popper.destroy();
      }
    };
  }, [collapsibleOpen, collapsibleProps.id, toggleProps.ref]);

  return (
    <FilterContainer>
      <FilterButton
        title={t(`facets.${facetKey}.title`)}
        value={active ? searchTerm : undefined}
        isActive={collapsibleOpen || active}
        buttonProps={toggleProps}
      />
      <CollapsibleContent {...collapsibleProps}>
        <div className="filter-button__panel-container">
          <FormsRow>
            <UserSingleSelectComboBoxForm2
              suggestUrl={suggestUrl}
              hiddenInputName=""
              labelTitle={t(`facets.${facetKey}.labelTitle`)}
              required
              onChange={setSelectedPerson}
            />
          </FormsRow>
        </div>
        <div className="filter-button__panel-interactions">
          <FormSubmitOrCancel
            acceptAction={acceptAction}
            cancelAction={closeAndFocusButton}
            headline={t('filterButton.headline')}
            hierarchy={2}
            hideHeadline
          />
        </div>
      </CollapsibleContent>
    </FilterContainer>
  );
}
